import type { SagaIterator } from "redux-saga";
import { call, fork, put, select, takeEvery, takeLeading } from "redux-saga/effects";
import type { ActionType } from "typesafe-actions";
import { getType } from "typesafe-actions";

import * as sessionApi from "@/shop/api/session";
import { updateIndustryCookie, updateLanguageCookie } from "@/shop/api/session-cookie";
import { fetchFavListsStart } from "@/shop/components/FavList/reducer/favListReducer/actions";
import { updateI18nextLanguage } from "@/shop/lib/updateI18nextLanguage";
import { fetchIndustryConfig } from "@/shop/reducers/industry";
import { detectPersonalizedFeatures, fetchLatestOrderHistory, fetchSalesRecommendation } from "@/shop/reducers/personalized";
import type { IStatusError } from "@/shop/types/errors";
import type { ICXmlSessionData, IDebitorInfo } from "@/shop/types/session";

import * as SessionActions from "./actions";
import { getSelectedDebitor } from "./selectors";
import type { ISessionData } from "./types";

/**
 * Adds punchout information to session data
 * @param originalSessionData
 * @returns A copy of the session data
 */
function* extendPunchoutSession(originalSessionData: ISessionData): SagaIterator<ISessionData> {
  // add punchout session id and type
  const sessionData = { ...originalSessionData, ...sessionApi.parsePunchoutSession() };

  // code below fails without customer
  if (!sessionData.selectedDebitor) {
    return sessionData;
  }

  // if this is a cxml session retrieve the additional cxml session data
  if (sessionData.punchoutType === "cxml" && sessionData.punchoutSessionId) {
    const cxmlSessionData = (yield call(sessionApi.getCXmlSessionData)) as ICXmlSessionData;
    Object.assign(sessionData, { cxmlSessionData });
  }

  // if this is an oci session retrieve the additional oci session data
  if (sessionData.punchoutType === "oci" && sessionData.punchoutSessionId) {
    const ociSessionData = (yield call(sessionApi.getOciSessionData)) as ICXmlSessionData;
    Object.assign(sessionData, { ociSessionData });
  }

  return sessionData;
}

/**
 * Handles backend errors for session
 */
function* handleSessionError(error: Error | IStatusError): SagaIterator {
  // determine the kind of error
  if ("statusCode" in error) {
    // backend error: distinguish error code
    // 401 = not authenticated, 403 = forbidden
    if ([401, 403].includes(error.statusCode!)) {
      yield call(sessionApi.loginOidc);
    }
  } else {
    // error caused by frontend, nothing to to
    console.error(error);
  }

  yield put(SessionActions.sessionSyncFail(error));
}

function* fetchSession(_: ActionType<typeof SessionActions.fetchSession>): SagaIterator {
  try {
    // restore previous URL from before oidc
    yield call(sessionApi.restorePreOidcUrl);

    // call the api to retrieve the session
    const originalSessionData = (yield call(sessionApi.getSession)) as ISessionData;

    // save industry cookie
    updateIndustryCookie(originalSessionData.industry, false);

    // extend session wit additional data
    const sessionData = (yield call(extendPunchoutSession, originalSessionData)) as ISessionData;

    // put action to save data in state
    yield put(SessionActions.sessionSyncSuccess(sessionData));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield call(handleSessionError, err);
  }
}

function* updateDebitor(action: ActionType<typeof SessionActions.updateDebitor>): SagaIterator<void> {
  try {
    const nextDebitorNumber: string = action.payload.debitorNumber;

    const currentDebitor = (yield select(getSelectedDebitor)) as IDebitorInfo;

    // call the api to retrieve the session
    const originalSessionData = (yield call(sessionApi.updateDebitor, nextDebitorNumber)) as ISessionData;
    // save industry cookie
    updateIndustryCookie(originalSessionData.industry, false);
    // extend session with additional data
    const sessionData = (yield call(extendPunchoutSession, originalSessionData)) as ISessionData;

    // put action to save data in state
    yield put(SessionActions.sessionSyncSuccess(sessionData));

    // reload the page to clear previous debtor data
    if (currentDebitor) {
      // set to true if punchout session needs to be preserved on debitor switch
      const preservePunchoutSession = !!action.payload.preservePunchoutSession;

      /// redirect to home to avoid getting to an invalid page
      let nextLocation = "/";

      if (preservePunchoutSession) {
        if (sessionData.punchoutType === "cxml") {
          nextLocation = `/?cxml=${sessionData.punchoutSessionId}`;
        } else if (sessionData.punchoutType === "oci") {
          nextLocation = `/?oci=${sessionData.punchoutSessionId}`;
        }
      }

      // redirect to landing page without "back" possibility
      window.location.replace(nextLocation);
    }
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield call(handleSessionError, err);
  }
}

function* updateLanguage(action: ActionType<typeof SessionActions.updateLanguage>): SagaIterator<void> {
  try {
    const lang = action.payload;

    // cimode is set only in the FE, no outgoing calls are required
    if (lang !== "cimode") {
      // update language cookie
      updateLanguageCookie(lang, false);
    }

    // The language selection is handled using the i18n internal state, not by the redux state
    // change language in i18next
    yield call(updateI18nextLanguage, lang);
    // reload app to refresh localized data
    if (lang !== "cimode") {
      window.location.reload();
    }
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield call(handleSessionError, err);
  }
}

/**
 * Deleted the session on the server and navigates to page specified in the session.
 */
function* logoutSession(_: ActionType<typeof SessionActions.logoutSession>): SagaIterator<void> {
  try {
    // TODO: evaluate if an existing cxml session should be deleted after logout
    yield call(sessionApi.logoutOidc); // terminate session
    // below code will never be called
  } catch (err) {
    console.error(err); // no clue what to do here :)
  }
}

function* detectSessionFeatures(): SagaIterator<void> {
  yield takeEvery(getType(SessionActions.sessionSyncSuccess), function* (action: ActionType<typeof SessionActions.sessionSyncSuccess>) {
    // detect features
    if (action.payload.selectedDebitor) {
      yield put(detectPersonalizedFeatures());
      yield put(fetchLatestOrderHistory());
      yield put(fetchSalesRecommendation());
      yield put(fetchIndustryConfig());
      yield put(fetchFavListsStart());
    }
  });
}

export default function* sessionSaga(): SagaIterator<void> {
  yield takeLeading(getType(SessionActions.fetchSession), fetchSession);
  yield takeLeading(getType(SessionActions.updateDebitor), updateDebitor);
  yield takeLeading(getType(SessionActions.updateLanguage), updateLanguage);
  yield takeLeading(getType(SessionActions.logoutSession), logoutSession);
  yield fork(detectSessionFeatures);
}
